/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import apiClient from '../../services/api';
import './StripePaymentModal.css';

interface StripePaymentModalProps {
  costInCents: number;
  costFormatted: string;
  onSuccess: (paymentIntentId: string) => void;
  onCancel: () => void;
}

const CheckoutForm: React.FC<{
  costFormatted: string;
  onSuccess: (paymentIntentId: string) => void;
  onCancel: () => void;
  clientSecret: string;
}> = ({ costFormatted, onSuccess, onCancel, clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { error: submitError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/bids',
        },
        redirect: 'if_required'
      });

      if (submitError) {
        setError(submitError.message || 'Payment failed');
        setProcessing(false);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent.id);
      } else {
        setError('Payment was not successful');
        setProcessing(false);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during payment');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-payment-form">
      <div className="payment-amount">
        <p>Amount: <strong>{costFormatted}</strong></p>
      </div>

      <PaymentElement />

      {error && (
        <div className="payment-error">
          {error}
        </div>
      )}

      <div className="payment-actions">
        <button
          type="button"
          className="cancel-button"
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="pay-button"
          disabled={!stripe || processing}
        >
          {processing ? 'Processing...' : 'Pay Now'}
        </button>
      </div>
    </form>
  );
};

const StripePaymentModal: React.FC<StripePaymentModalProps> = ({
  costInCents,
  costFormatted,
  onSuccess,
  onCancel
}) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Load Stripe publishable key and create payment intent
    const initialize = async () => {
      try {
        const configResponse = await apiClient.getStripeConfig();
        setStripePromise(loadStripe(configResponse.publishableKey));

        const paymentIntentResponse = await apiClient.createPaymentIntent(
          costInCents,
          'usd',
          'Bid Generation Credits'
        );

        if (paymentIntentResponse.success && paymentIntentResponse.clientSecret) {
          setClientSecret(paymentIntentResponse.clientSecret);
        } else {
          setError('Failed to initialize payment');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to initialize payment');
      }
    };

    initialize();
  }, [costInCents]);

  if (error) {
    return (
      <div className="modal-overlay">
        <div className="modal-content stripe-payment-modal">
          <h2>Payment Error</h2>
          <div className="modal-body">
            <p className="error-message">{error}</p>
          </div>
          <div className="modal-actions">
            <button className="cancel-button" onClick={onCancel}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!stripePromise || !clientSecret) {
    return (
      <div className="modal-overlay">
        <div className="modal-content stripe-payment-modal">
          <h2>Processing Payment</h2>
          <div className="modal-body">
            <p>Initializing payment...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content stripe-payment-modal">
        <h2>Purchase Credits</h2>
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <CheckoutForm
            costFormatted={costFormatted}
            onSuccess={onSuccess}
            onCancel={onCancel}
            clientSecret={clientSecret}
          />
        </Elements>
      </div>
    </div>
  );
};

export default StripePaymentModal;
